import { Button, Menu, Typography, Stack, Skeleton } from '@mui/material'
import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { ChevronLeft, ChevronRight, ExpandLess, ExpandMore } from '@mui/icons-material'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { formatDateText } from './helpers'
import ServiceDate from './ServiceDate'
import { capitalise } from '@/helpers/strings'
import MealIcon from './MealIcon'

const canteenServiceSelectionUserDataFragment = graphql(`
  fragment CanteenServiceSelection_UserDataFragment on User {
    accountLocation {
      timezone
    }
    upcomingCcMenus {
      date
      ...CanteenServiceDate_MealServiceDataFragment
    }
  }
`)

type Props = {
  serviceData: FragmentType<typeof canteenServiceSelectionUserDataFragment>
  selectedDate: string
  selectedMeal: string
  onChangeSelectedService: (input: { meal: string; date: string }) => void
}

const ServiceSelectionGraphql: React.FC<Props> = ({
  selectedDate,
  selectedMeal,
  serviceData,
  onChangeSelectedService,
}) => {
  const { accountLocation, upcomingCcMenus } = useFragment(
    canteenServiceSelectionUserDataFragment,
    serviceData,
  )

  const timezone = accountLocation?.timezone

  const [selectedWeekStart, setSelectedWeekStart] = useState(moment().startOf('isoWeek').toDate())
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  useEffect(() => {
    if (!timezone) return
    setSelectedWeekStart(moment.tz(selectedDate, timezone).startOf('isoWeek').toDate())
  }, [timezone, selectedDate])

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleNextWeekSelect = () => {
    if (!timezone || !upcomingCcMenus || !upcomingCcMenus.length) return
    const latestWeekEndInData = moment
      .tz(upcomingCcMenus[upcomingCcMenus.length - 1].date, timezone)
      .endOf('isoWeek')
    setSelectedWeekStart((prev) => {
      const nextWeekStart = moment.tz(prev, timezone).add(1, 'week')
      if (nextWeekStart.isAfter(latestWeekEndInData)) return prev
      return nextWeekStart.toDate()
    })
  }

  const handlePreviousWeekSelect = () => {
    if (!timezone || !upcomingCcMenus || !upcomingCcMenus.length) return
    const earliestWeekStartInData = moment.tz(upcomingCcMenus[0].date, timezone).startOf('isoWeek')
    setSelectedWeekStart((prev) => {
      const previousWeekStart = moment.tz(prev, timezone).subtract(1, 'week')
      if (previousWeekStart.isBefore(earliestWeekStartInData)) return prev
      return previousWeekStart.toDate()
    })
  }

  if (!timezone) {
    return (
      <Button
        fullWidth
        variant="outlined"
        disabled
        sx={{
          display: 'block',
          borderColor: 'divider',
          minHeight: '40px',
          boxShadow: 'none',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Skeleton width="100%" />
          <ExpandMore color="action" />
        </Stack>
      </Button>
    )
  }

  const handleServiceChange = (date: Date, meal: string) => {
    handleClose()
    onChangeSelectedService({ date: moment.tz(date, timezone).format('YYYY-MM-DD'), meal })
  }

  const selectedDateInTz = moment.tz(selectedDate, timezone).startOf('day')
  const selectedWeekEnd = moment.tz(selectedWeekStart, timezone).endOf('isoWeek')
  const dataRangeToShow = upcomingCcMenus.filter((serviceDate) =>
    moment(serviceDate.date).isBetween(selectedWeekStart, selectedWeekEnd, undefined, '[]'),
  )
  const open = Boolean(anchorEl)

  return (
    <>
      <Button
        onClick={handleClick}
        aria-haspopup="true"
        id="basic-button"
        variant="outlined"
        size="medium"
        fullWidth
        sx={{
          borderColor: 'divider',
          minHeight: '40px',
          boxShadow: 'none',
          overflow: 'hidden',
        }}
        startIcon={<MealIcon type={selectedMeal} />}
        endIcon={open ? <ExpandLess color="action" /> : <ExpandMore color="action" />}
      >
        <Typography noWrap paddingLeft={1} variant="body1">
          {formatDateText(selectedDateInTz.toDate(), timezone)}
          {' ('}
          {capitalise(selectedMeal)}
          {') '}
        </Typography>
      </Button>
      <Menu
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          maxHeight: '400px',
        }}
        anchorEl={anchorEl}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        id="basic-menu"
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          borderBottom={2}
          borderColor="primary.main"
        >
          <Button
            sx={{ justifyContent: 'left' }}
            variant="text"
            onClick={() => handlePreviousWeekSelect()}
          >
            <ChevronLeft color="action" />
          </Button>
          <Typography>
            {moment.tz(selectedWeekStart, timezone).format('Do MMM')} -{' '}
            {moment.tz(selectedWeekStart, timezone).endOf('isoWeek').format('Do MMM')}
          </Typography>
          <Button
            sx={{ justifyContent: 'right' }}
            variant="text"
            onClick={() => handleNextWeekSelect()}
          >
            <ChevronRight color="action" />
          </Button>
        </Stack>
        {dataRangeToShow.map((dataForDate) => (
          <ServiceDate
            key={dataForDate.date}
            onChange={handleServiceChange}
            isSelectedDate={moment
              .tz(dataForDate.date, timezone)
              .startOf('day')
              .isSame(selectedDateInTz)}
            selectedMeal={selectedMeal}
            serviceData={dataForDate}
            timezone={timezone}
          />
        ))}
      </Menu>
    </>
  )
}

export default ServiceSelectionGraphql
